import { useState, useRef, useEffect, useMemo } from 'react';
import { AsYouType, parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js/min';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Loader2, ArrowLeft } from 'lucide-react';
import CountryPicker from '@/components/CountryPicker';
import MagicLinkFallback from '@/components/MagicLinkFallback';
import { guessDefaultCountry } from '@/utils/countries';

/**
 * PhoneAuthPanel — 2-step phone (SMS) auth with country picker + live formatting.
 *
 * Props:
 *   mode              'signin' (default) → POST /auth/phone/verify-code
 *                     'link'   → POST /auth/phone/link (auth required)
 *   referralCode      optional referral code to attribute the signup to
 *   onAuthSuccess     (resData) => void   called after successful verify
 *
 * UX:
 *   - Country selector to the left of the input (flag + dial code).
 *   - Local number field is live-formatted with `AsYouType` for the picked
 *     country. Backend still receives a clean +E.164 string.
 *   - Country picker defaults to the browser's locale country; user can
 *     always change it.
 */
const inputCls =
  'rounded-r-xl bg-[#F8F9FA] border-black/[0.06] text-[#1A1A1A] placeholder:text-zinc-400 focus-visible:ring-[#EF4444]/50 focus-visible:border-[#EF4444]/50 px-4 py-3.5 text-sm';

const otpCls =
  'rounded-xl bg-[#F8F9FA] border-black/[0.06] text-[#1A1A1A] placeholder:text-zinc-400 focus-visible:ring-[#EF4444]/50 focus-visible:border-[#EF4444]/50 px-4 py-3.5 text-sm';

export default function PhoneAuthPanel({ mode = 'signin', referralCode, onAuthSuccess }) {
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [country, setCountry] = useState(() => guessDefaultCountry());
  const [national, setNational] = useState(''); // user-entered digits (formatted for display)
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  // After 3 consecutive "Invalid phone number" errors, offer a magic-link
  // fallback. Not shown in 'link' mode (existing users already have an
  // account and can just use email/password to sign in another way).
  const [invalidCount, setInvalidCount] = useState(0);
  const [showFallback, setShowFallback] = useState(false);
  const codeInputRef = useRef(null);

  // Focus OTP input when step changes
  useEffect(() => {
    if (step === 'code') setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [step]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  // Live-format the national number for the selected country.
  const formatted = useMemo(() => {
    if (!national) return '';
    try {
      return new AsYouType(country).input(national);
    } catch {
      return national;
    }
  }, [national, country]);

  // Compose the +E.164 number we submit to the backend.
  const e164 = useMemo(() => {
    const digits = national.replace(/\D/g, '');
    if (!digits) return '';
    try {
      const parsed = parsePhoneNumberFromString(`+${getCountryCallingCode(country)}${digits}`, country);
      return parsed?.isValid() ? parsed.number : '';
    } catch {
      return '';
    }
  }, [national, country]);

  const handleNationalChange = (raw) => {
    // Strip everything except digits so the AsYouType formatter can rebuild.
    setNational(raw.replace(/[^\d]/g, ''));
  };

  const sendCode = async (isResend = false) => {
    if (!e164) {
      const next = invalidCount + 1;
      setInvalidCount(next);
      if (next >= 3 && mode !== 'link') setShowFallback(true);
      toast.error('Enter a valid phone number for the selected country');
      return;
    }
    setSending(true);
    try {
      await api.post('/auth/phone/send-code', { phone: e164 });
      setInvalidCount(0);   // successful send → reset counter
      toast.success(isResend ? 'Code re-sent' : "We've sent a 6-digit code to your phone.");
      setStep('code');
      setResendCountdown(30);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not send code';
      // Server-side "Invalid phone number" (from libphonenumber on the
      // backend) also counts against the fallback threshold.
      if (String(detail).toLowerCase().includes('invalid phone')) {
        const next = invalidCount + 1;
        setInvalidCount(next);
        if (next >= 3 && mode !== 'link') setShowFallback(true);
      }
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your SMS');
      return;
    }
    setVerifying(true);
    try {
      const endpoint = mode === 'link' ? '/auth/phone/link' : '/auth/phone/verify-code';
      const payload = { phone: e164, code: code.trim() };
      if (referralCode) payload.referral_code = referralCode.trim().toUpperCase();
      const res = await api.post(endpoint, payload);
      if (mode === 'link') {
        toast.success('Phone number linked to your account!');
      } else {
        toast.success(res.data.created ? 'Welcome to Meet to Keep!' : 'Signed in');
      }
      onAuthSuccess?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid or expired code';
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="phone-auth-panel">
      {step === 'phone' && (
        <>
          <div className="flex items-stretch">
            <CountryPicker value={country} onChange={setCountry} />
            <Input
              data-testid="phone-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="Phone number"
              className={`${inputCls} flex-1 border-l-0`}
              value={formatted}
              onChange={(e) => handleNationalChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendCode(false)}
            />
          </div>
          <p className="text-[11px] text-zinc-500 -mt-1 px-1 leading-relaxed">
            We&apos;ll text you a 6-digit verification code. Standard SMS rates apply.
          </p>
          <button
            type="button"
            onClick={() => sendCode(false)}
            data-testid="phone-send-code-btn"
            disabled={sending}
            className="w-full brand-gradient text-white py-3.5 rounded-xl text-sm font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.25)] disabled:opacity-50 active:scale-[0.98]"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Send code'}
          </button>
          {showFallback && (
            <MagicLinkFallback onCancel={() => { setShowFallback(false); setInvalidCount(0); }} />
          )}
        </>
      )}

      {step === 'code' && (
        <>
          <button
            type="button"
            onClick={() => {
              setStep('phone');
              setCode('');
            }}
            data-testid="phone-edit-btn"
            className="text-[12px] font-semibold text-[#4B4B52] hover:text-[#B91C1C] inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={12} /> {e164 || formatted}
          </button>
          <Input
            ref={codeInputRef}
            data-testid="phone-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            className={`${otpCls} tracking-[0.5em] text-center font-semibold`}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && verifyCode()}
          />
          <button
            type="button"
            onClick={verifyCode}
            data-testid="phone-verify-code-btn"
            disabled={verifying || code.length !== 6}
            className="w-full brand-gradient text-white py-3.5 rounded-xl text-sm font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.25)] disabled:opacity-50 active:scale-[0.98]"
          >
            {verifying ? (
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            ) : mode === 'link' ? (
              'Link phone number'
            ) : (
              'Verify & continue'
            )}
          </button>
          <div className="flex items-center justify-center pt-1">
            <button
              type="button"
              data-testid="phone-resend-code-btn"
              disabled={resendCountdown > 0 || sending}
              onClick={() => sendCode(true)}
              className="text-[12px] font-semibold text-[#4B4B52] hover:text-[#B91C1C] disabled:text-zinc-400 disabled:cursor-not-allowed transition-colors"
            >
              {resendCountdown > 0 ? `Resend code in ${resendCountdown}s` : "Didn't get it? Resend"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
